import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { scrollState, mouseState } from "@/lib/scrollStore";

const smooth = (t) => t * t * (3 - 2 * t);

function Particles() {
  const pointsRef = useRef();
  const groupRef = useRef();

  const sprite = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 64;
    c.height = 64;
    const ctx = c.getContext("2d");
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.35, "rgba(255,240,200,0.9)");
    g.addColorStop(1, "rgba(255,220,150,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(c);
    tex.needsUpdate = true;
    return tex;
  }, []);

  const data = useMemo(() => {
    // Bei "Bewegung reduzieren" bleibt das Feld als ruhiges Sternenbild
    // stehen: weniger Punkte, keine Bewegung. Ganz abschalten wäre falsch -
    // die Seite sähe dann leer aus -, aber das Wabern entfällt.
    const wenigerBewegung =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const count = wenigerBewegung
      ? 900
      : typeof window !== "undefined" && window.innerWidth < 768
        ? 1800
        : 4200;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const ring = new Float32Array(count * 3);
    const lines = new Float32Array(count * 3);
    const sphere = new Float32Array(count * 3);
    const gold = new THREE.Color("#D4AF37");
    const bright = new THREE.Color("#FFE9A8");
    const deep = new THREE.Color("#6b5318");

    for (let i = 0; i < count; i++) {
      const j = i * 3;
      const a = Math.random() * Math.PI * 2;
      const r = 5.4 + (Math.random() - 0.5) * 1.8;
      ring[j] = Math.cos(a) * r + (Math.random() - 0.5) * 0.7;
      ring[j + 1] = Math.sin(a) * r * 0.6 + (Math.random() - 0.5) * 0.7;
      ring[j + 2] = (Math.random() - 0.5) * 2.4;

      const line = i % 14;
      const t = Math.random();
      const ang = (line / 14) * Math.PI * 0.9 - Math.PI * 0.45;
      const spread = (line - 6.5) * 1.05;
      lines[j] = (t - 0.5) * 20 * Math.cos(ang);
      lines[j + 1] = spread + (Math.random() - 0.5) * 0.18;
      lines[j + 2] = (t - 0.5) * 10 * Math.sin(ang) + (line % 2 === 0 ? 1.6 : -1.6);

      const phi = Math.acos(1 - (2 * (i + 0.5)) / count);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;
      const rr = 3.4 + (Math.random() - 0.5) * 0.5;
      sphere[j] = rr * Math.sin(phi) * Math.cos(theta);
      sphere[j + 1] = rr * Math.cos(phi);
      sphere[j + 2] = rr * Math.sin(phi) * Math.sin(theta);

      positions[j] = ring[j];
      positions[j + 1] = ring[j + 1];
      positions[j + 2] = ring[j + 2];

      const c = Math.random() < 0.12 ? bright : Math.random() < 0.5 ? gold : deep;
      colors[j] = c.r;
      colors[j + 1] = c.g;
      colors[j + 2] = c.b;
    }
    return { count, positions, colors, ring, lines, sphere };
  }, []);

  useFrame((state, delta) => {
    const pts = pointsRef.current;
    if (!pts) return;
    const arr = pts.geometry.attributes.position.array;
    const p = Math.min(Math.max(scrollState.progress * 2.15, 0), 2);
    const { ring, lines, sphere, count } = data;

    for (let i = 0; i < count; i++) {
      const j = i * 3;
      let tx, ty, tz;
      if (p < 1) {
        const k = smooth(p);
        tx = ring[j] + (lines[j] - ring[j]) * k;
        ty = ring[j + 1] + (lines[j + 1] - ring[j + 1]) * k;
        tz = ring[j + 2] + (lines[j + 2] - ring[j + 2]) * k;
      } else {
        const k = smooth(p - 1);
        tx = lines[j] + (sphere[j] - lines[j]) * k;
        ty = lines[j + 1] + (sphere[j + 1] - lines[j + 1]) * k;
        tz = lines[j + 2] + (sphere[j + 2] - lines[j + 2]) * k;
      }
      arr[j] += (tx - arr[j]) * 0.05;
      arr[j + 1] += (ty - arr[j + 1]) * 0.05;
      arr[j + 2] += (tz - arr[j + 2]) * 0.05;
    }
    pts.geometry.attributes.position.needsUpdate = true;

    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.06;
      groupRef.current.rotation.z = scrollState.progress * 0.5;
    }
    const cam = state.camera;
    cam.position.x += (mouseState.x * 1.1 - cam.position.x) * 0.03;
    cam.position.y += (-mouseState.y * 0.7 - cam.position.y) * 0.03;
    cam.position.z += (10 - scrollState.progress * 3 - cam.position.z) * 0.05;
    cam.lookAt(0, 0, 0);
  });

  return (
    <group ref={groupRef}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[data.positions, 3]} />
          <bufferAttribute attach="attributes-color" args={[data.colors, 3]} />
        </bufferGeometry>
        <pointsMaterial
          size={0.14}
          map={sprite}
          alphaMap={sprite}
          vertexColors
          transparent
          opacity={0.85}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          sizeAttenuation
        />
      </points>
    </group>
  );
}

function Rings() {
  const r1 = useRef();
  const r2 = useRef();
  const r3 = useRef();
  useFrame((state, delta) => {
    if (r1.current) {
      r1.current.rotation.x += delta * 0.12;
      r1.current.rotation.y += delta * 0.08;
    }
    if (r2.current) {
      r2.current.rotation.y -= delta * 0.06;
      r2.current.rotation.z += delta * 0.05;
    }
    if (r3.current) {
      r3.current.rotation.x += delta * 0.1;
      r3.current.rotation.y -= delta * 0.07;
    }
  });
  return (
    <>
      <mesh ref={r1} position={[0, 0, -2]}>
        <torusGeometry args={[5.2, 0.012, 8, 128]} />
        <meshBasicMaterial color="#D4AF37" transparent opacity={0.5} />
      </mesh>
      <mesh ref={r2} position={[0, 0, -2]} rotation={[0.6, 0.4, 0]}>
        <torusGeometry args={[6.4, 0.008, 8, 128]} />
        <meshBasicMaterial color="#8a6a1f" transparent opacity={0.35} />
      </mesh>
      <mesh ref={r3} position={[-4.4, 1.6, -4]}>
        <icosahedronGeometry args={[1.1, 0]} />
        <meshBasicMaterial wireframe color="#D4AF37" transparent opacity={0.12} />
      </mesh>
      <mesh position={[4.6, -1.8, -5]}>
        <torusKnotGeometry args={[0.9, 0.26, 90, 10]} />
        <meshBasicMaterial wireframe color="#D4AF37" transparent opacity={0.1} />
      </mesh>
    </>
  );
}

export default function ParticleField() {
  // frameloop "demand" zeichnet nur einmal statt sechzig Mal pro Sekunde.
  // Das Bild bleibt, die Bewegung nicht - und der Rechner bleibt kühl.
  const wenigerBewegung =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div className="fixed inset-0 z-0 pointer-events-none" data-testid="particle-canvas">
      <Canvas
        frameloop={wenigerBewegung ? "demand" : "always"}
        camera={{ position: [0, 0, 10], fov: 55 }}
        dpr={[1, 1.8]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <fog attach="fog" args={["#050505", 9, 24]} />
        <Particles />
        <Rings />
      </Canvas>
    </div>
  );
}
